import { useState, useEffect } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Package, 
  Search,
  Plus,
  Loader2
} from "lucide-react";
import type { InventoryItem } from "@/types/api";
import { DashboardLayout } from "@/components/layout/dashboard-layout";

const Inventory = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<InventoryItem[]>([]);

  useEffect(() => {
    const fetchInventoryItems = async () => {
      setIsLoading(true);
      setError("");
      
      try {
        // In a real app, we would use the API service
        // const response = await inventoryService.getItems();
        // setInventoryItems(response.data);
        
        // Simulate API call with mock data
        setTimeout(() => {
          const mockItems: InventoryItem[] = [
            {
              id: 1,
              name: "Wireless Mouse",
              description: "Ergonomic wireless mouse with long battery life",
              category: "Electronics",
              quantity: 45,
              unitPrice: 24.99,
              totalValue: 1124.55,
              location: "Warehouse A",
              sku: "WM-001",
              createdAt: "2023-05-15T10:30:00Z",
              updatedAt: "2023-06-20T14:15:00Z"
            },
            {
              id: 2,
              name: "USB-C Adapter",
              description: "Multi-port USB-C adapter for laptops",
              category: "Electronics",
              quantity: 78,
              unitPrice: 19.99,
              totalValue: 1559.22,
              location: "Warehouse A",
              sku: "UA-002",
              createdAt: "2023-04-10T09:45:00Z",
              updatedAt: "2023-06-18T11:20:00Z"
            },
            {
              id: 3,
              name: "HDMI Cable",
              description: "High-speed HDMI cable, 6ft length",
              category: "Cables",
              quantity: 32,
              unitPrice: 14.99,
              totalValue: 479.68,
              location: "Warehouse B",
              sku: "HC-003",
              createdAt: "2023-05-22T13:15:00Z",
              updatedAt: "2023-06-15T16:30:00Z"
            },
            {
              id: 4,
              name: "Laptop Stand",
              description: "Adjustable aluminum laptop stand",
              category: "Accessories",
              quantity: 21,
              unitPrice: 29.99,
              totalValue: 629.79,
              location: "Warehouse A",
              sku: "LS-004",
              createdAt: "2023-06-01T08:20:00Z",
              updatedAt: "2023-06-22T10:45:00Z"
            },
            {id: 5,
              name: "Wireless Keyboard",
              description: "Slim wireless keyboard with numeric keypad",
              category: "Electronics",
              quantity: 36,
              unitPrice: 39.99,
              totalValue: 1439.64,
              location: "Warehouse A",
              sku: "WK-005",
              createdAt: "2023-05-05T11:10:00Z",
              updatedAt: "2023-06-19T15:25:00Z"
            },
            {
              id: 6,
              name: "Bluetooth Speaker",
              description: "Portable Bluetooth speaker with 12-hour battery",
              category: "Audio",
              quantity: 18,
              unitPrice: 49.99,
              totalValue: 899.82,
              location: "Warehouse C",
              sku: "BS-006",
              createdAt: "2023-06-10T14:30:00Z",
              updatedAt: "2023-06-21T09:15:00Z"
            },
            {
              id: 7,
              name: "Monitor Stand",
              description: "Dual monitor stand with adjustable height",
              category: "Accessories",
              quantity: 12,
              unitPrice: 59.99,
              totalValue: 719.88,
              location: "Warehouse B",
              sku: "MS-007",
              createdAt: "2023-05-18T16:45:00Z",
              updatedAt: "2023-06-17T13:20:00Z"
            },
            {
              id: 8,
              name: "Webcam",
              description: "1080p HD webcam with microphone",
              category: "Electronics",
              quantity: 27,
              unitPrice: 44.99,
              totalValue: 1214.73,
              location: "Warehouse A",
              sku: "WC-008",
              createdAt: "2023-06-05T10:15:00Z",
              updatedAt: "2023-06-20T11:30:00Z"
            }
          ];
          
          setInventoryItems(mockItems);
          setFilteredItems(mockItems);
          setIsLoading(false);
        }, 1000);
      } catch (err) {
        console.error("Error fetching inventory items:", err);
        setError("Failed to load inventory items. Please try again later.");
        setIsLoading(false);
      }
    };

    fetchInventoryItems();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredItems(inventoryItems);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = inventoryItems.filter(item => 
        item.name.toLowerCase().includes(query) || 
        item.description.toLowerCase().includes(query) || 
        item.category.toLowerCase().includes(query) ||
        item.sku.toLowerCase().includes(query)
      );
      setFilteredItems(filtered);
    }
  }, [searchQuery, inventoryItems]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const getStockStatusBadge = (quantity: number) => {
    if (quantity <= 0) {
      return <Badge variant="destructive">Out of Stock</Badge>;
    } else if (quantity < 20) {
      return <Badge variant="outline" className="bg-amber-100 text-amber-800 hover:bg-amber-100">Low Stock</Badge>;
    } else {
      return <Badge variant="outline" className="bg-green-100 text-green-800 hover:bg-green-100">In Stock</Badge>;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Inventory</h1>
            <p className="text-muted-foreground">Manage your inventory items</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search inventory..."
                className="pl-8 w-full sm:w-[250px] md:w-[300px]"
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
            
            <Button asChild>
              <Link to="/inventory/add">
                <Plus size={16} className="mr-2" />
                Add Item
              </Link>
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 size={40} className="animate-spin text-primary" />
          </div>
        ) : error ? (
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="text-center text-red-500">
                <p>{error}</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {filteredItems.length === 0 ? (
              <div className="text-center py-12">
                <Package size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No items found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery ? "Try a different search term" : "Add your first inventory item"}
                </p>
                {searchQuery && (
                  <Button 
                    variant="outline" 
                    onClick={() => setSearchQuery("")}
                  >
                    Clear Search
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredItems.map((item) => (
                  <Card key={item.id} className="overflow-hidden">
                    <CardHeader className="p-4 pb-2">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg">{item.name}</CardTitle>
                        {getStockStatusBadge(item.quantity)}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">SKU: {item.sku}</p>
                    </CardHeader>
                    <CardContent className="p-4 pt-2">
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{item.description}</p>
                      
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Category</p>
                          <p className="font-medium">{item.category}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Location</p>
                          <p className="font-medium">{item.location}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Quantity</p>
                          <p className="font-medium">{item.quantity}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Unit Price</p>
                          <p className="font-medium">${item.unitPrice.toFixed(2)}</p>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex justify-between items-center">
                        <p className="text-sm font-semibold">
                          Total Value: ${item.totalValue.toFixed(2)}
                        </p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          asChild
                        >
                          <Link to={`/inventory/${item.id}`}>View Details</Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Inventory;